import type { ToleranceEvaluationDto } from './project-controls.js';
import type { BusinessCaseViability } from './business-case.js';

export const MonitoringCheckType = {
  Daily: 'DAILY',
  Event: 'EVENT',
  WeeklyHighlight: 'WEEKLY_HIGHLIGHT',
  GatePrep: 'GATE_PREP',
  BenefitReview: 'BENEFIT_REVIEW',
} as const;
export const MonitoringCheckTypeValues = Object.values(MonitoringCheckType);
export type MonitoringCheckType = (typeof MonitoringCheckType)[keyof typeof MonitoringCheckType];

export const MonitoringRunStatus = {
  Pending: 'PENDING',
  Running: 'RUNNING',
  Done: 'DONE',
  Failed: 'FAILED',
  Skipped: 'SKIPPED',
} as const;
export const MonitoringRunStatusValues = Object.values(MonitoringRunStatus);
export type MonitoringRunStatus = (typeof MonitoringRunStatus)[keyof typeof MonitoringRunStatus];

export const MonitoringRunTrigger = {
  Cron: 'CRON',
  Event: 'EVENT',
  CatchUp: 'CATCH_UP',
} as const;
export const MonitoringRunTriggerValues = Object.values(MonitoringRunTrigger);
export type MonitoringRunTrigger = (typeof MonitoringRunTrigger)[keyof typeof MonitoringRunTrigger];

export const MonitoringAlertKind = {
  ToleranceException: 'TOLERANCE_EXCEPTION',
  ToleranceWarning: 'TOLERANCE_WARNING',
  DataStale: 'DATA_STALE',
  CannotEvaluate: 'CANNOT_EVALUATE',
  JustificationLost: 'JUSTIFICATION_LOST',
  GatePrepDue: 'GATE_PREP_DUE',
  PendingDecision: 'PENDING_DECISION',
  PausedTooLong: 'PAUSED_TOO_LONG',
  DeliveryFailed: 'DELIVERY_FAILED',
  BenefitReviewDue: 'BENEFIT_REVIEW_DUE',
} as const;
export const MonitoringAlertKindValues = Object.values(MonitoringAlertKind);
export type MonitoringAlertKind = (typeof MonitoringAlertKind)[keyof typeof MonitoringAlertKind];

export const MonitoringAlertStatus = {
  Open: 'OPEN',
  Acknowledged: 'ACKNOWLEDGED',
  Resolved: 'RESOLVED',
} as const;
export const MonitoringAlertStatusValues = Object.values(MonitoringAlertStatus);
export type MonitoringAlertStatus = (typeof MonitoringAlertStatus)[keyof typeof MonitoringAlertStatus];

export const AlertDeliveryStatus = {
  Pending: 'PENDING',
  Sent: 'SENT',
  Failed: 'FAILED',
} as const;
export const AlertDeliveryStatusValues = Object.values(AlertDeliveryStatus);
export type AlertDeliveryStatus = (typeof AlertDeliveryStatus)[keyof typeof AlertDeliveryStatus];

export const BenefitReviewStatus = {
  Pending: 'PENDING',
  Recorded: 'RECORDED',
} as const;
export const BenefitReviewStatusValues = Object.values(BenefitReviewStatus);
export type BenefitReviewStatus = (typeof BenefitReviewStatus)[keyof typeof BenefitReviewStatus];

export const MonitoringQueueStatus = {
  Available: 'AVAILABLE',
  Unavailable: 'UNAVAILABLE',
} as const;
export const MonitoringQueueStatusValues = Object.values(MonitoringQueueStatus);
export type MonitoringQueueStatus = (typeof MonitoringQueueStatus)[keyof typeof MonitoringQueueStatus];

export interface MonitoringRunDto {
  id: string;
  checkType: MonitoringCheckType;
  periodKey: string;
  status: MonitoringRunStatus;
  trigger: MonitoringRunTrigger;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

export interface MonitoringAlertDto {
  id: string;
  projectId: string;
  kind: MonitoringAlertKind;
  status: MonitoringAlertStatus;
  dedupeKey: string;
  displayTitle: string | null;
  displayBody: string | null;
  payload: Record<string, unknown>;
  reminderCount: number;
  escalatedAt: string | null;
  lastNotifiedAt: string | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface AlertDeliveryDto {
  id: string;
  alertId: string;
  recipientUserId: string;
  attempt: number;
  status: AlertDeliveryStatus;
  error: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface ProjectBenefitReviewDto {
  id: string;
  dueDate: string;
  status: BenefitReviewStatus;
  outcome: string | null;
  recordedByUserId: string | null;
  recordedAt: string | null;
}

export interface RecordBenefitReviewDto {
  outcome: string;
}

export interface HighlightReportDto {
  periodKey: string;
  generatedAt: string;
  exception: boolean;
  paused: boolean;
  eac: number | null;
  viability: BusinessCaseViability | null;
  evaluation: ToleranceEvaluationDto[];
  narrativeMarkdown?: string | null;
}

export interface ProjectMonitoringDto {
  lastCheckAt: string | null;
  lastCheckStatus: MonitoringRunStatus | null;
  alerts: MonitoringAlertDto[];
  highlight: HighlightReportDto | null;
  benefitReviews: ProjectBenefitReviewDto[];
  failedDeliveries: AlertDeliveryDto[];
}

export interface MonitoringHealthDto {
  lastSuccessfulTickAt: string | null;
  overdueRunCount: number;
  queueStatus: MonitoringQueueStatus;
  failedDeliveryCount: number;
}
